import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { authService } from '../../services/authService';
import { creditReportService } from '../../services/creditReportService';
import Recaptcha from '../../components/common/Recaptcha';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { formatCurrency, formatDate, humanizeStatus } from '../../utils/helpers';

export const PENDING_REFERENCE_KEY = 'credit_report_reference';

// Must match backend missing_profile_fields — RAAS+ needs these from the verified profile.
const PROFILE_FIELDS = [['date_of_birth', 'date of birth'], ['gender', 'gender'], ['phone_number', 'phone number']];

const maskBvn = (bvn) => (bvn ? `•••••••${bvn.slice(-4)}` : '—');

function DetailRow({ label, value }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', padding: '0.45rem 0', fontSize: '0.88rem' }}>
      <span style={{ color: 'var(--text-3)' }}>{label}</span>
      <span style={{ fontWeight: 600, textAlign: 'right' }}>{value || '—'}</span>
    </div>
  );
}

export default function CreditReport() {
  const { data: user, isLoading: profileLoading } = useQuery({
    queryKey: ['profile'],
    queryFn: () => authService.getProfile().then(r => r.data.data),
  });
  // Same source of truth as the backend: the KYC record must be approved.
  const kycStatus = user?.kyc_status || 'not_submitted';
  const kycVerified = kycStatus === 'approved';
  const missing = PROFILE_FIELDS.filter(([field]) => !user?.[field]).map(([, label]) => label);

  const [reportId, setReportId] = useState('');
  const [captchaToken, setCaptchaToken] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const { data: types = [], isLoading, isError: typesError } = useQuery({
    queryKey: ['credit-report-types'],
    queryFn: () => creditReportService.types().then(r => r.data.data),
  });
  const { data: orders = [] } = useQuery({
    queryKey: ['credit-report-orders'],
    queryFn: () => creditReportService.orders().then(r => r.data.data),
    enabled: kycVerified,
  });

  // Products come live from RAAS+; default to the first one until the customer picks.
  const selected = types.find(t => t.report_id === reportId) || types[0];
  const oneOff = types.filter(t => !t.is_subscription);
  const subscriptions = types.filter(t => t.is_subscription);
  const optionLabel = (t) => `${t.name} — ${formatCurrency(t.price_naira)}${t.is_subscription ? ' / year' : ''}`;
  const kycMessage = {
    pending:      'Your KYC has been submitted and is waiting to be reviewed. You can get your credit report once it has been approved.',
    under_review: 'Your KYC is being reviewed. You can get your credit report once it has been approved.',
    rejected:     'Your KYC was not approved. Please resubmit it on Profile & KYC to get your credit report.',
    resubmit:     'Your KYC needs to be resubmitted. Please update it on Profile & KYC to get your credit report.',
  }[kycStatus] || "Credit reports are pulled using your verified BVN, so they're only available once your KYC has been approved.";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      const { data } = await creditReportService.purchase({ report_id: selected.report_id, recaptcha_token: captchaToken });
      try { sessionStorage.setItem(PENDING_REFERENCE_KEY, data.data.reference); } catch { /* storage unavailable */ }
      window.location.assign(data.data.authorization_url);
    } catch (err) {
      const d = err.response?.data;
      setError(d?.message || 'Could not start your purchase. Please try again.');
      setLoading(false);
    }
  };

  if (isLoading || profileLoading) return <LoadingSpinner />;

  return (
    <div style={{ maxWidth: 640 }}>
      <div className="page-header">
        <h1>Credit Report</h1>
        <p>Get your official CRC credit report, emailed to you right after payment.</p>
      </div>

      <div className="card" style={{ marginBottom: '1.25rem' }}>
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label className="form-label" htmlFor="report_id">Choose a report</label>
          {typesError ? (
            <div className="alert alert-error"><span>⚠</span><span>Could not load the available reports. Please refresh the page.</span></div>
          ) : (
            <select id="report_id" className="form-input" value={selected?.report_id || ''} onChange={e => setReportId(e.target.value)}>
              {oneOff.length > 0 && (
                <optgroup label="One-off reports">
                  {oneOff.map(t => <option key={t.report_id} value={t.report_id}>{optionLabel(t)}</option>)}
                </optgroup>
              )}
              {subscriptions.length > 0 && (
                <optgroup label="1-year subscriptions">
                  {subscriptions.map(t => <option key={t.report_id} value={t.report_id}>{optionLabel(t)}</option>)}
                </optgroup>
              )}
            </select>
          )}
          {selected && (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginTop: '0.875rem' }}>
                <span style={{ color: 'var(--text-2)' }}>Price</span>
                <span style={{ fontSize: '1.5rem', fontWeight: 800 }}>
                  {formatCurrency(selected.price_naira)}
                  {selected.is_subscription && <span style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-3)' }}> / year</span>}
                </span>
              </div>
              <p style={{ fontSize: '0.82rem', color: 'var(--text-3)', margin: '0.5rem 0 0' }}>
                {selected.is_subscription
                  ? 'A 1-year subscription: you get a confirmation email with your start and end dates, then alerts and reports by email throughout the year.'
                  : 'A one-off report, emailed to you as a PDF shortly after payment.'}
              </p>
            </>
          )}
        </div>
      </div>

      {!kycVerified || missing.length > 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '2rem 1.5rem' }}>
          <div style={{ fontSize: '2rem', marginBottom: '0.75rem' }}>🔒</div>
          <h2 style={{ fontSize: '1.15rem', fontWeight: 700, marginBottom: '0.5rem' }}>
            {!kycVerified ? (['pending', 'under_review'].includes(kycStatus) ? 'KYC approval pending' : 'Complete your KYC first') : 'Complete your profile first'}
          </h2>
          <p style={{ color: 'var(--text-2)', fontSize: '0.9rem', marginBottom: '1.5rem' }}>
            {kycVerified
              ? `Your report is pulled using your verified details. Add your ${missing.join(', ')} on Profile & KYC to continue.`
              : kycMessage}
          </p>
          <Link to="/profile" className="btn btn-primary">Go to Profile & KYC →</Link>
        </div>
      ) : (
        <form className="card" onSubmit={handleSubmit}>
          {error && <div className="alert alert-error"><span>⚠</span><span>{error}</span></div>}

          <div style={{ border: '1px solid var(--border)', borderRadius: 12, padding: '0.5rem 1rem', marginBottom: '1rem' }}>
            <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.04em', padding: '0.4rem 0' }}>
              Your verified details
            </div>
            <DetailRow label="Name" value={user?.full_name} />
            <DetailRow label="BVN" value={maskBvn(user?.bvn)} />
            <DetailRow label="Date of birth" value={formatDate(user?.date_of_birth)} />
            <DetailRow label="Gender" value={humanizeStatus(user?.gender)} />
            <DetailRow label="Phone" value={user?.phone_number} />
            <DetailRow label="Report sent to" value={user?.email} />
          </div>

          <p style={{ fontSize: '0.8rem', color: 'var(--text-3)', margin: '0.25rem 0 0' }}>
            Details wrong? Update them on <Link to="/profile">Profile & KYC</Link>. Payment is handled securely by Paystack.
          </p>

          <div style={{ display: 'flex', justifyContent: 'center', margin: '1.25rem 0' }}>
            <Recaptcha onChange={setCaptchaToken} />
          </div>

          <button type="submit" className="btn btn-primary btn-lg" style={{ width: '100%' }} disabled={loading || !captchaToken || !selected}>
            {loading ? 'Redirecting to payment…' : `Pay ${selected ? formatCurrency(selected.price_naira) : ''} →`}
          </button>
        </form>
      )}

      {orders.length > 0 && (
        <div className="card" style={{ marginTop: '1.25rem' }}>
          <h2 style={{ fontSize: '0.95rem', fontWeight: 700, marginBottom: '0.75rem' }}>Your recent reports</h2>
          {orders.map(o => (
            <div key={o.reference} style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', padding: '0.5rem 0', borderTop: '1px solid var(--border)', fontSize: '0.85rem' }}>
              <span>{o.report_name}{o.is_subscription ? ' (1-year subscription)' : ''} · {formatDate(o.created_at)}</span>
              <span style={{ color: 'var(--text-2)' }}>{humanizeStatus(o.status)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
